import { useState } from "react";
import { Link } from "react-router";

import { DataTable } from "./DataTable.jsx";
import { LogConsole } from "./LogConsole.jsx";
import { ProjectIcon } from "./ProjectIcon.jsx";
import { StatusBadge, StatusIcon } from "./StatusBadge.jsx";
import { Elapsed, Modal, ago, duration } from "./ui.jsx";

/**
 * The one way a list of deployments is shown.
 *
 * The overview, the Deployments page and a project's Deployments tab each had
 * their own table, and each drifted: one hid the commit below `lg`, one put a
 * "success" pill on the phone row where the message should have been, one
 * showed no time at all. Now they share the columns and the log modal, and
 * differ in one thing — `project` says the rows span projects, so the slug
 * leads and the phone row takes two lines.
 *
 * The rule: in a table a Status column carries the pill; on a line — a phone
 * row, a card, a header — the glyph leads, a running one counts beside it,
 * then what shipped, then when. Never a pill on a line.
 */
export function DeploymentTable({ deployments, project = false, iconOf, caption, empty = null }) {
    const [open, setOpen] = useState(null);

    // The row as the poll last saw it, so the modal's pill and counter move on
    // with the deploy rather than staying as they were when it was opened.
    const opened = open ? deployments.find((d) => d.id === open.id) ?? open : null;

    const columns = [
        {
            key: "deployment",
            header: project ? "Project" : "#",
            primary: true,
            cell: (deployment) => project ? (
                <span className="flex min-w-0 items-center gap-2">
                    <ProjectIcon slug={deployment.slug} hash={iconOf?.(deployment.slug)} size={16} />
                    <Link
                        to={`/projects/${deployment.slug}`}
                        className="link truncate"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {deployment.slug}
                    </Link>
                    <span className="muted shrink-0 font-mono text-xs">#{deployment.number}</span>
                </span>
            ) : (
                <span className="font-mono">#{deployment.number}</span>
            ),
            mobileCell: (deployment) => <DeploymentLine deployment={deployment} project={project} iconOf={iconOf} />
        },
        {
            key: "status",
            header: "Status",
            // The glyph on the phone row says it.
            mobileHidden: true,
            cell: (deployment) => <StatusBadge status={deployment.status} since={deployment.startedAt} />
        },
        {
            key: "commit",
            header: "Commit",
            fill: true,
            cell: (deployment) => (
                <span className="flex min-w-0 items-center gap-1.5">
                    {deployment.commit?.shortSha ? <code className="font-mono text-xs">{deployment.commit.shortSha}</code> : null}
                    <span className="muted truncate">{deployment.commit?.message ?? deployment.trigger}</span>
                </span>
            )
        },
        { key: "actor", header: "By", hideBelow: "lg", cell: (deployment) => deployment.actor },
        {
            key: "when",
            header: "When",
            // On the phone row already, at the end of the first line.
            mobileHidden: true,
            cell: (deployment) => <span className="muted whitespace-nowrap">{ago(deployment.startedAt ?? deployment.createdAt)}</span>
        },
        {
            key: "took",
            header: "Took",
            align: "right",
            cell: (deployment) => <span className="muted whitespace-nowrap">{duration(deployment.durationMs)}</span>
        }
    ];

    // The phone row is a button, and a link cannot sit inside one — so the way
    // to the project from a phone is a line in the opened row.
    if (project) {
        columns.push({
            key: "project",
            header: "Project",
            mobileOnly: true,
            cell: (deployment) => <Link to={`/projects/${deployment.slug}`} className="link">{deployment.slug}</Link>
        });
    }

    return (
        <>
            <DataTable
                rows={deployments}
                rowKey={(deployment) => deployment.id}
                onRowClick={setOpen}
                caption={caption}
                empty={empty}
                columns={columns}
            />

            <Modal open={Boolean(opened)} onClose={() => setOpen(null)} title={`${opened?.slug} #${opened?.number}`} width="max-w-4xl">
                {opened ? (
                    <div className="space-y-3">
                        <div className="muted flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                            <StatusBadge status={opened.status} since={opened.startedAt} />
                            <span>{opened.trigger} by {opened.actor}</span>
                            {opened.node ? <span>Node {opened.node}</span> : null}
                            {opened.releaseDir ? <code className="font-mono">{opened.releaseDir.split("/").at(-1)}</code> : null}
                        </div>
                        {opened.reason ? <p className="text-sm text-red-600 dark:text-red-400">{opened.reason}</p> : null}
                        <LogConsole deploymentId={opened.id} status={opened.status} since={opened.startedAt} />
                    </div>
                ) : null}
            </Modal>
        </>
    );
}

/**
 * A deployment as a phone row reads it.
 *
 * The line is one sentence wide and no column header explains it, so it has
 * to earn itself: the glyph says how it went, a running one counts beside it,
 * then the number, the commit message, and when. "#77" beside a "success"
 * pill said neither, and the pill cost most of the width.
 *
 * Across projects the slug has to be there too, and slug, number and message
 * do not fit in one line — the message takes a second.
 */
function DeploymentLine({ deployment, project, iconOf }) {
    const message = deployment.commit?.message ?? deployment.trigger;

    return (
        <span className="flex min-w-0 items-start gap-2">
            {/* Boxed to the first line's height, so on two lines the glyph sits on the first rather than between them. */}
            <span className="flex h-5 shrink-0 items-center">
                <StatusIcon status={deployment.status} />
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm">
                    {/* No pill on the phone row, so the counter stands beside the glyph — in the
                        text column, so a running row's second line lines up with its neighbours'. */}
                    {deployment.status === "running" ? <Elapsed since={deployment.startedAt} className="shrink-0 text-xs text-blue-600 dark:text-blue-400" /> : null}
                    {project ? (
                        <>
                            <ProjectIcon slug={deployment.slug} hash={iconOf?.(deployment.slug)} size={16} />
                            <span className="truncate">{deployment.slug}</span>
                        </>
                    ) : null}
                    <span className="muted shrink-0 font-mono text-xs">#{deployment.number}</span>
                    {project ? null : <span className="truncate">{message}</span>}
                    <span className="muted ml-auto shrink-0 text-xs">{ago(deployment.startedAt ?? deployment.createdAt)}</span>
                </span>
                {project ? <span className="muted block truncate text-xs">{message}</span> : null}
            </span>
        </span>
    );
}
